import { useEffect, useMemo, useRef, useState } from 'react';
import { FiArrowRight, FiCheckCircle, FiChevronDown, FiChevronRight, FiCircle, FiPlus } from 'react-icons/fi';
import { SearchableSelect } from '../../ui';
import styles from './ImportReconciliationStep.module.css';

/**
 * Etapa de conciliação do wizard de importação, usada duas vezes: uma para
 * casar as categorias da planilha com os GRUPOS do Financeiro Diário e outra
 * para casar as subcategorias com as CATEGORIAS dentro do grupo escolhido.
 *
 * As duas telas são a mesma coisa com rótulos diferentes, então vivem no mesmo
 * componente: o que muda é o vocabulário e o agrupamento em seções, que só a
 * etapa de categorias usa.
 */

/** Valor do select que abre o campo de nome para criar um destino novo. */
const CRIAR_NOVO = '__criar_novo__';

export interface ReconciliationItem {
    key: string;
    /** Nome na planilha. Nulo, na etapa de grupos, quando não havia grupo lá. */
    planilhaNome: string | null;
    tipo: 'ENTRADA' | 'SAIDA';
    sugestao: { id: string | null; nome: string; matchType: 'exact' | 'suggested' | 'none' };
    /** Linha de apoio abaixo do nome ("1 subcategorias · 18 lançamentos"). */
    detalhe: string;
    /** Só na etapa de categorias: em que seção o item entra. */
    secaoKey?: string;
    /** Só na etapa de grupos: nomes das categorias que caem neste grupo. */
    filhos?: string[];
}

export interface ReconciliationOption {
    id: string;
    nome: string;
}

export interface ReconciliationSection {
    key: string;
    titulo: string;
    subtitulo: string;
}

export interface ReconciliationChoiceState {
    destinoId?: string;
    novoNome?: string;
}

interface Props {
    modo: 'grupos' | 'categorias';
    itens: ReconciliationItem[];
    /** Destinos disponíveis para cada item, já filtrados por tipo/grupo. */
    opcoesPara: (item: ReconciliationItem) => ReconciliationOption[];
    escolhas: Record<string, ReconciliationChoiceState>;
    onChange: (key: string, escolha: ReconciliationChoiceState) => void;
    /** Seções da etapa de categorias. Vazio na etapa de grupos. */
    secoes?: ReconciliationSection[];
}

/**
 * Situação de um item.
 *
 *   'existente'  -> vai para algo que já está no plano de contas
 *   'nova'       -> será criada, com o nome que veio da planilha
 *   'aproximada' -> o sistema achou um destino parecido, mas quem decide é o usuário
 *   'pendente'   -> não há destino e nem nome de origem para propor
 *
 * Numa planilha de produção a maioria das contas ainda não existe no FD, então
 * "será criada com o seu próprio nome" é um padrão bom e não pode custar um
 * clique por item. Pendente sobra só para o balde sem nome de origem, em que
 * quem inventaria o nome seria o sistema.
 *
 * Correspondência aproximada é o único caso em que o sistema sozinho poderia
 * jogar lançamentos numa conta que não é a da planilha ("Sucata Everton" indo
 * parar em "Sucata Everton 0713" quando são contas diferentes). Por isso ela
 * não vale como conciliada: fica travando o avanço até o usuário confirmar ou
 * escolher outro destino.
 */
export type SituacaoItem = 'existente' | 'nova' | 'aproximada' | 'pendente';

export function situacaoDoItem(item: ReconciliationItem, escolha: ReconciliationChoiceState | undefined): SituacaoItem {
    if (escolha?.destinoId) return 'existente';
    if (escolha?.novoNome?.trim()) return 'nova';
    if (item.sugestao.matchType === 'suggested') return 'aproximada';
    if (item.sugestao.matchType === 'exact') return 'existente';
    return item.planilhaNome === null ? 'pendente' : 'nova';
}

/** Um item está resolvido quando não exige mais decisão para seguir. */
export function isConciliado(item: ReconciliationItem, escolha: ReconciliationChoiceState | undefined): boolean {
    const situacao = situacaoDoItem(item, escolha);
    return situacao !== 'pendente' && situacao !== 'aproximada';
}

/** Rótulo do destino que valerá se o usuário não mexer em mais nada. */
function destinoAtual(
    item: ReconciliationItem,
    escolha: ReconciliationChoiceState | undefined,
    opcoes: ReconciliationOption[],
): { nome: string; novo: boolean } | null {
    if (escolha?.destinoId) {
        const opcao = opcoes.find((o) => o.id === escolha.destinoId);
        if (opcao) return { nome: opcao.nome, novo: false };
    }
    const novo = escolha?.novoNome?.trim();
    if (novo) return { nome: novo, novo: true };
    if (item.sugestao.matchType === 'none' && item.planilhaNome === null) return null;
    return { nome: item.sugestao.nome, novo: item.sugestao.id === null };
}

const TEXTOS = {
    grupos: {
        titulo: 'Organize os grupos de destino',
        descricao:
            'As categorias da planilha viram grupos no FD. Só a correspondência exata já vem resolvida. Nas aproximadas, confirme o destino; nos itens pendentes, escolha um destino ou crie um novo.',
        buscar: 'Buscar grupo',
        placeholderBusca: 'Buscar na planilha ou no FD',
        contador: (feitos: number, total: number) => `${feitos} de ${total} grupos conciliados`,
        criar: 'Criar novo grupo',
        novoNomeLabel: 'Nome do novo grupo',
        destinoPrefixo: 'Destino',
        semOrigem: 'Sem grupo na planilha',
        origemLabel: 'Categoria da planilha',
        resumoExistente: 'já existem no seu plano de contas',
        resumoNova: 'serão criados',
        semOrigemAjuda: 'Estas categorias aparecem nos lançamentos, mas não estão cadastradas na aba "Plano de Contas" do arquivo — por isso não têm grupo de origem.',
        verFilhos: (n: number) => `O que está neste grupo? ${n} categoria(s)`,
    },
    categorias: {
        titulo: 'Concilie as categorias de cada grupo',
        descricao:
            'As subcategorias da planilha viram categorias dentro dos grupos escolhidos. Só a correspondência exata já vem resolvida. Nas aproximadas, confirme o destino; nos itens pendentes, escolha um destino ou crie um novo.',
        buscar: 'Buscar categoria ou grupo',
        placeholderBusca: 'Buscar na planilha ou no FD',
        contador: (feitos: number, total: number) => `${feitos} de ${total} categorias conciliadas`,
        criar: 'Criar nova categoria',
        novoNomeLabel: 'Nome da nova categoria',
        destinoPrefixo: 'Destino',
        semOrigem: 'Sem grupo na planilha',
        origemLabel: 'Subcategoria da planilha',
        resumoExistente: 'já existem no seu plano de contas',
        resumoNova: 'serão criadas',
        semOrigemAjuda: 'Sem grupo de origem na planilha.',
        verFilhos: (n: number) => `${n} item(ns)`,
    },
} as const;

const SELO: Record<SituacaoItem, { classe: (s: Record<string, string>) => string; texto: (modo: 'grupos' | 'categorias') => string }> = {
    existente: { classe: (s) => s.badgeOk, texto: () => 'Conciliado' },
    nova: { classe: (s) => s.badgeNova, texto: (modo) => (modo === 'grupos' ? 'Grupo novo' : 'Categoria nova') },
    aproximada: { classe: (s) => s.badgeAproximada, texto: () => 'Confirme o destino' },
    pendente: { classe: (s) => s.badgePendente, texto: () => 'Pendente' },
};

function normalizar(valor: string): string {
    return valor.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

export function ImportReconciliationStep({ modo, itens, opcoesPara, escolhas, onChange, secoes = [] }: Props) {
    const textos = TEXTOS[modo];
    const topoRef = useRef<HTMLDivElement>(null);
    const [busca, setBusca] = useState('');
    const [soPendentes, setSoPendentes] = useState(false);
    const [recolhidas, setRecolhidas] = useState<Set<string>>(new Set());
    const [editandoNome, setEditandoNome] = useState<Record<string, string>>({});

    const situacoes = useMemo(
        () => itens.map((item) => situacaoDoItem(item, escolhas[item.key])),
        [itens, escolhas],
    );
    const pendentes = itens.filter((item) => situacaoDoItem(item, escolhas[item.key]) === 'pendente');
    const existentes = situacoes.filter((situacao) => situacao === 'existente').length;
    const novas = situacoes.filter((situacao) => situacao === 'nova').length;
    const resolvidos = itens.length - pendentes.length;
    const progresso = itens.length === 0 ? 100 : Math.round((resolvidos / itens.length) * 100);

    const visiveis = useMemo(() => {
        const termo = normalizar(busca.trim());
        return itens.filter((item) => {
            if (soPendentes && isConciliado(item, escolhas[item.key])) return false;
            if (!termo) return true;
            const destino = destinoAtual(item, escolhas[item.key], opcoesPara(item));
            const alvo = `${item.planilhaNome ?? textos.semOrigem} ${destino?.nome ?? ''}`;
            return normalizar(alvo).includes(termo);
        });
    }, [itens, escolhas, busca, soPendentes, opcoesPara, textos.semOrigem]);

    // O corpo do modal e um scroll so, entao ao trocar de etapa a posicao
    // anterior permanecia e a etapa nova abria no meio, escondendo o resumo.
    useEffect(() => {
        topoRef.current?.scrollIntoView({ block: 'start' });
    }, [modo]);

    const alternarSecao = (key: string) => {
        setRecolhidas((atual) => {
            const proxima = new Set(atual);
            if (proxima.has(key)) proxima.delete(key);
            else proxima.add(key);
            return proxima;
        });
    };

    const renderItem = (item: ReconciliationItem) => {
        const escolha = escolhas[item.key];
        const opcoes = opcoesPara(item);
        const destino = destinoAtual(item, escolha, opcoes);
        const situacao = situacaoDoItem(item, escolha);
        const conciliado = isConciliado(item, escolha);
        const criandoNovo = editandoNome[item.key] !== undefined;

        const valorSelect = escolha?.destinoId
            ?? (criandoNovo || escolha?.novoNome ? CRIAR_NOVO : (item.sugestao.id ?? (item.sugestao.matchType === 'none' ? '' : CRIAR_NOVO)));

        return (
            <div key={item.key} className={`${styles.item} ${conciliado ? styles.itemOk : styles.itemPendente}`}>
                <div className={styles.itemHeader}>
                    <span className={styles.itemTipo}>
                        {modo === 'categorias' ? 'Categoria de ' : ''}
                        {item.tipo === 'ENTRADA' ? 'entrada' : 'saída'}
                    </span>
                    <span className={SELO[situacao].classe(styles)}>
                        {conciliado ? <FiCheckCircle size={13} /> : <FiCircle size={13} />}
                        {SELO[situacao].texto(modo)}
                    </span>
                </div>

                <div className={styles.itemCorpo}>
                    <div className={styles.coluna}>
                        <span className={styles.colunaTitulo}>Como está na planilha</span>
                        <strong className={styles.origemNome}>{item.planilhaNome ?? textos.semOrigem}</strong>
                        <span className={styles.origemMeta}>
                            {item.planilhaNome === null ? textos.semOrigemAjuda : textos.origemLabel}
                        </span>
                        <span className={styles.origemMeta}>{item.detalhe}</span>

                        {item.filhos && item.filhos.length > 0 && (
                            <details className={styles.filhos}>
                                <summary>{textos.verFilhos(item.filhos.length)}</summary>
                                <ul>
                                    {item.filhos.map((filho) => (
                                        <li key={filho}>{filho}</li>
                                    ))}
                                </ul>
                            </details>
                        )}
                    </div>

                    <FiArrowRight className={styles.seta} size={18} aria-hidden />

                    <div className={styles.coluna}>
                        <span className={styles.colunaTitulo}>Como ficará no Financeiro Diário</span>

                        <SearchableSelect
                            options={[
                                ...opcoes.map((opcao) => ({ value: opcao.id, label: opcao.nome })),
                                { value: CRIAR_NOVO, label: `+ ${textos.criar}` },
                            ]}
                            value={valorSelect}
                            onChange={(valor) => {
                                if (valor === CRIAR_NOVO) {
                                    const sugerido = escolha?.novoNome ?? (item.planilhaNome ?? item.sugestao.nome);
                                    setEditandoNome((atual) => ({ ...atual, [item.key]: sugerido }));
                                    onChange(item.key, { novoNome: sugerido });
                                    return;
                                }
                                setEditandoNome((atual) => {
                                    const proxima = { ...atual };
                                    delete proxima[item.key];
                                    return proxima;
                                });
                                onChange(item.key, { destinoId: valor });
                            }}
                            placeholder="Selecione um destino ou crie um novo"
                            fullWidth
                            dropdownVariant="inline"
                        />

                        {item.sugestao.matchType === 'exact' && !escolha && (
                            <span className={styles.ajuda}>Correspondência exata: destino sugerido para sua confirmação. Você pode alterar a escolha.</span>
                        )}
                        {situacao === 'aproximada' && (
                            <div className={styles.aproximadaBox}>
                                <span className={styles.ajudaAproximada}>
                                    Correspondência aproximada com <strong>{item.sugestao.nome}</strong>. Os nomes não são iguais,
                                    então confirme que é a mesma conta ou escolha outro destino.
                                </span>
                                {item.sugestao.id && (
                                    <button
                                        type="button"
                                        className={styles.aproximadaConfirmar}
                                        onClick={() => onChange(item.key, { destinoId: item.sugestao.id! })}
                                    >
                                        <FiCheckCircle size={14} /> Confirmar {item.sugestao.nome}
                                    </button>
                                )}
                            </div>
                        )}

                        {criandoNovo ? (
                            <div className={styles.novoBox}>
                                <label className={styles.novoLabel} htmlFor={`novo-${item.key}`}>{textos.novoNomeLabel}</label>
                                <input
                                    id={`novo-${item.key}`}
                                    className={styles.novoInput}
                                    value={editandoNome[item.key]}
                                    onChange={(evento) => {
                                        const valor = evento.target.value;
                                        setEditandoNome((atual) => ({ ...atual, [item.key]: valor }));
                                        onChange(item.key, { novoNome: valor });
                                    }}
                                    autoFocus
                                />
                                <span className={styles.ajuda}>Edite o nome como quer vê-lo no FD. Será criado apenas ao confirmar a importação.</span>
                                <button
                                    type="button"
                                    className={styles.novoConcluir}
                                    onClick={() => {
                                        setEditandoNome((atual) => {
                                            const proxima = { ...atual };
                                            delete proxima[item.key];
                                            return proxima;
                                        });
                                    }}
                                    disabled={!editandoNome[item.key]?.trim()}
                                >
                                    Concluir nome
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                className={styles.criarLink}
                                onClick={() => {
                                    const sugerido = item.planilhaNome ?? item.sugestao.nome;
                                    setEditandoNome((atual) => ({ ...atual, [item.key]: sugerido }));
                                    onChange(item.key, { novoNome: sugerido });
                                }}
                            >
                                <FiPlus size={13} /> {textos.criar}
                            </button>
                        )}

                        {destino && (
                            <span className={styles.destinoResumo}>
                                <FiCheckCircle size={13} />
                                {destino.novo ? `Novo ${modo === 'grupos' ? 'grupo' : 'categoria'}: ` : `${textos.destinoPrefixo}: `}
                                <strong>{destino.nome}</strong>
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className={styles.container} ref={topoRef}>
            <h3 className={styles.titulo}>{textos.titulo}</h3>
            <p className={styles.descricao}>{textos.descricao}</p>

            <div className={`${styles.progressoCard} ${pendentes.length === 0 ? styles.progressoOk : ''}`}>
                <div className={styles.progressoTopo}>
                    <strong>{textos.contador(resolvidos, itens.length)}</strong>
                    <span>{progresso}%</span>
                </div>
                <div className={styles.progressoTrilha}>
                    <div className={styles.progressoBarra} style={{ width: `${progresso}%` }} />
                </div>

                <div className={styles.resumo}>
                    <span>
                        <strong>{existentes}</strong> {textos.resumoExistente}
                    </span>
                    <span>
                        <strong>{novas}</strong> {textos.resumoNova}
                    </span>
                    {pendentes.length > 0 && (
                        <span className={styles.resumoPendente}>
                            <strong>{pendentes.length}</strong> sem destino
                        </span>
                    )}
                </div>

                <span className={styles.progressoAjuda}>
                    {pendentes.length === 0
                        ? `Tudo pronto nesta etapa. O que não existe no seu plano de contas será criado com o nome da planilha — confira antes de continuar.`
                        : `${pendentes.length} item(ns) sem destino. Selecione ou crie os destinos para continuar.`}
                </span>
            </div>

            <div className={styles.filtros}>
                <label className={styles.buscaLabel} htmlFor={`busca-${modo}`}>{textos.buscar}</label>
                <input
                    id={`busca-${modo}`}
                    className={styles.busca}
                    value={busca}
                    onChange={(evento) => setBusca(evento.target.value)}
                    placeholder={textos.placeholderBusca}
                />
                <label className={styles.somentePendentes}>
                    <input type="checkbox" checked={soPendentes} onChange={(evento) => setSoPendentes(evento.target.checked)} />
                    Só pendentes ({pendentes.length})
                </label>
            </div>

            {visiveis.length === 0 ? (
                <div className={styles.vazio}>
                    <FiCheckCircle size={20} />
                    <span>{soPendentes ? 'Nenhuma pendência nesta etapa' : 'Nenhum item corresponde à busca'}</span>
                </div>
            ) : secoes.length > 0 ? (
                secoes.map((secao) => {
                    const daSecao = visiveis.filter((item) => item.secaoKey === secao.key);
                    if (daSecao.length === 0) return null;
                    const total = itens.filter((item) => item.secaoKey === secao.key);
                    const feitos = total.filter((item) => isConciliado(item, escolhas[item.key])).length;
                    const recolhida = recolhidas.has(secao.key);

                    return (
                        <section key={secao.key} className={styles.secao}>
                            <button type="button" className={styles.secaoHeader} onClick={() => alternarSecao(secao.key)}>
                                {recolhida ? <FiChevronRight size={16} /> : <FiChevronDown size={16} />}
                                <div className={styles.secaoTitulos}>
                                    <strong>{secao.titulo}</strong>
                                    <span>{secao.subtitulo}</span>
                                </div>
                                <span className={feitos === total.length ? styles.badgeOk : styles.badgePendente}>
                                    {feitos}/{total.length} conciliadas
                                </span>
                            </button>
                            {!recolhida && <div className={styles.secaoItens}>{daSecao.map(renderItem)}</div>}
                        </section>
                    );
                })
            ) : (
                visiveis.map(renderItem)
            )}

            <p className={styles.rodape}>
                Suas escolhas só serão aplicadas ao confirmar a importação.
                {modo === 'grupos' && ' Ao alterar um grupo, confira as novas sugestões para as categorias dele.'}
            </p>
        </div>
    );
}
